import { getInitialState } from '../../../../global-store/subjects/user-preferences';
import { getLabel } from '../../../../labels/labelling-service';
import logger from '../../../../services/logging/logging-service';
import { satelliteTracker } from '../../../../services/tracking/satellite-tracking-service';
import { WPN_ANALYTICS_EVENTS, trackPageView, EDIT_PAGE_VIEW_EVENT } from '../../../../utils/analytics';
import { allowBodyScroll } from '../../../../utils/body-scroll';
import { RESPONSIVE_OFFSETS } from '../../../../utils/constants';
import { scrollToElement } from '../../../../utils/dom-utils';
import { removeLocalStorage } from '../../../../utils/local-storage';
import {
  getAnalyticsCategories,
  getAnalyticsTopics,
  setOptionsFromSdk,
  setOptionsToSdk,
  initDefaultOptions,
} from '../../../../utils/push-notifications-utils';
import {
  doWidgetMutations as doWidgetMutationsNewsletters,
  widgetMutationsSuccessCallback as widgetMutationsSuccessCallbackNewsletters,
} from '../../newsletter/api';
import { doWidgetMutationInPage as doWidgetMutationInPageParsely } from '../../parsely/api';

const getHandleConfirmCancel =
  ({ htmlProps, widget, state, actions, WPNCtx, parselyCtx, newslettersCtx }) =>
  () => {
    switch (state.activeBlock) {
      case widget.blockTypes.notifications:
        {
          const selectBoxes = htmlProps.blocks.filter((block) => block.type === widget.blockTypes.notifications)[0].data
            .selectBoxes;
          actions.setConfirmBottomDialog({ isShown: false });
          actions.setActiveBlock(null);
          if (WPNCtx.state.sdk?.channel?.optedIn) {
            setOptionsFromSdk({ sdk: WPNCtx.state.sdk, selectBoxes, actions: WPNCtx.actions });
          } else {
            initDefaultOptions({ selectBoxes, actions: WPNCtx.actions, allUnchecked: true });
          }
        }
        break;

      case widget.blockTypes.myTeam:
        parselyCtx.myTeam.actions.selectPreferences(parselyCtx.myTeam.state.userPreferences);
        actions.setConfirmBottomDialog({ isShown: false });
        actions.setActiveBlock(null);
        break;

      case widget.blockTypes.mySport:
        parselyCtx.mySport.actions.selectPreferences(parselyCtx.mySport.state.userPreferences);
        actions.setConfirmBottomDialog({ isShown: false });
        actions.setActiveBlock(null);
        break;

      case widget.blockTypes.myLocalNews:
        parselyCtx.myLocalNews.actions.selectPreferences(parselyCtx.myLocalNews.state.userPreferences);
        actions.setConfirmBottomDialog({ isShown: false });
        actions.setActiveBlock(null);
        break;

      case widget.blockTypes.newsletters:
        newslettersCtx.actions.selectNewsletters(
          newslettersCtx.state.dataSubscriptions.list
            .filter((newsletter) => newsletter.isSubscribed)
            .map((newsletter) => newsletter.id)
        );
        actions.setConfirmBottomDialog({ isShown: false });
        actions.setActiveBlock(null);
        newslettersCtx.actions.showUnsubscribeModal(false);
        break;

      default:
        break;
    }
  };

const getHandleConfirmAccept =
  ({ htmlProps, widget, state, actions, parselyCtx, WPNCtx, newslettersCtx }) =>
  async () => {
    switch (state.activeBlock) {
      case widget.blockTypes.notifications:
        try {
          WPNCtx.actions.setIsLoading(true);
          actions.setConfirmBottomDialog({ isShown: false });
          actions.setActiveBlock(null);
          const selectBoxes = htmlProps.blocks.filter((block) => block.type === widget.blockTypes.notifications)[0].data
            .selectBoxes;
          await setOptionsToSdk({ sdk: WPNCtx.state.sdk, selectBoxes, selectedOptions: WPNCtx.state.selectedOptions });
          WPNCtx.actions.setIsLoading(false);
          actions.setNotificationStatus({ isShown: true, message: getLabel('status.notification.message.WPN') });
          actions.setNotificationStatus({ isShown: false }, 3000);

          const categoriesAndTopics = {
            category: getAnalyticsCategories(selectBoxes, WPNCtx.state.selectedOptions),
            name: getAnalyticsTopics(selectBoxes, WPNCtx.state.selectedOptions),
          };
          satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.EDIT, categoriesAndTopics);
        } catch (error) {
          logger.error(error);
          WPNCtx.actions.setIsLoading(false);
        }
        break;

      case widget.blockTypes.myTeam:
        {
          const onSuccessCallback = () => {
            actions.setNotificationStatus({
              isShown: true,
              message: getLabel('status.notification.message.set.myteam'),
            });
            actions.setNotificationStatus({ isShown: false }, 3000);
          };
          actions.setActiveBlock(null);
          actions.setConfirmBottomDialog({ isShown: false });
          doWidgetMutationInPageParsely({
            actions: parselyCtx.myTeam.actions,
            commonVariables: parselyCtx.myTeam.derivedValues.commonVariables,
            options: parselyCtx.myTeam.state.selectedOptions,
            widget: parselyCtx.myTeam.widget,
            onSuccessCallback,
          });
        }
        break;

      case widget.blockTypes.mySport:
        {
          const onSuccessCallback = () => {
            actions.setNotificationStatus({
              isShown: true,
              message: getLabel('status.notification.message.set.mysport'),
            });
            actions.setNotificationStatus({ isShown: false }, 3000);
          };
          actions.setActiveBlock(null);
          actions.setConfirmBottomDialog({ isShown: false });
          doWidgetMutationInPageParsely({
            actions: parselyCtx.mySport.actions,
            commonVariables: parselyCtx.mySport.derivedValues.commonVariables,
            options: parselyCtx.mySport.state.selectedOptions,
            widget: parselyCtx.mySport.widget,
            onSuccessCallback,
          });
        }
        break;

      case widget.blockTypes.myLocalNews:
        {
          const onSuccessCallback = () => {
            actions.setNotificationStatus({
              isShown: true,
              message: getLabel('status.notification.message.set.mylocalnews'),
            });
            actions.setNotificationStatus({ isShown: false }, 3000);
          };
          actions.setActiveBlock(null);
          actions.setConfirmBottomDialog({ isShown: false });
          doWidgetMutationInPageParsely({
            actions: parselyCtx.myLocalNews.actions,
            commonVariables: parselyCtx.myLocalNews.derivedValues.commonVariables,
            options: parselyCtx.myLocalNews.state.selectedOptions,
            widget: parselyCtx.myLocalNews.widget,
            onSuccessCallback,
          });
        }
        break;

      case widget.blockTypes.newsletters:
        try {
          const newsLettersFromHtml = newslettersCtx.htmlProps.newsLetters;

          const topics = newsLettersFromHtml
            .filter((newsletter) => newslettersCtx.state.selectedOptionsIds.includes(newsletter.id))
            .map((newsletter) => newsletter.topic);

          const uniqueTopicPerSelectedNewsletter = [...new Set(topics)].join('|');
          satelliteTracker.trackInWeb('newsletter_conferma_iscrizione', {
            status: 'iscritto nl',
            topic: uniqueTopicPerSelectedNewsletter,
          });
          actions.setActiveBlock(null);
          actions.setConfirmBottomDialog({ isShown: false });
          newslettersCtx.actions.showUnsubscribeModal(false);
          newslettersCtx.actions.showNewslettersFullScreen(false);
          allowBodyScroll('MOBILE');
          doWidgetMutationsNewsletters({
            widget: newslettersCtx.widget,
            actions: newslettersCtx.actions,
            state: newslettersCtx.state,
            onSuccessCallback: () =>
              widgetMutationsSuccessCallbackNewsletters({
                state: newslettersCtx.state,
                actions: newslettersCtx.actions,
                pageActions: actions,
                widget: newslettersCtx.widget,
              }),
          });
        } catch (error) {
          logger.error(error);
        }
        break;

      default:
        break;
    }
  };

const getHandleFeatureListCtaClick = () => () => {
  const responsiveOffset = RESPONSIVE_OFFSETS.default;
  const $element = document.querySelector('.s-sky-it-feature-lists-wrapper');
  scrollToElement({ $element, top: false, responsiveOffset });
};

const getHandleTabClick = (actions, widget) => (index) => {
  actions.selectTab(index);
  const tabName = widget.tabsNames[index];
  trackPageView(tabName);
  window.digitalData.page.pageInfo.intcmp = '';
  removeLocalStorage('intcmp');
};

const getHandleModalTooltipClose = (actions) => () => {
  actions.setModalTooltip(getInitialState().modalTooltip);
};

const getHandleModalTooltipPageClick = (actions, state) => (selectedPage) => {
  actions.setModalTooltip({ ...state.modalTooltip, selectedPage });
};

const getHandlePanelNoDataClick = (actions) => () => {
  actions.selectTab(2);
};

export const getHandlers = ({ htmlProps, widget, state, actions, WPNCtx, parselyCtx, newslettersCtx }) => ({
  handleConfirmCancel: getHandleConfirmCancel({
    htmlProps,
    widget,
    state,
    actions,
    WPNCtx,
    parselyCtx,
    newslettersCtx,
  }),
  handleConfirmAccept: getHandleConfirmAccept({
    htmlProps,
    widget,
    state,
    actions,
    WPNCtx,
    parselyCtx,
    newslettersCtx,
  }),
  handleFeatureListCtaClick: getHandleFeatureListCtaClick(),
  handleTabClick: getHandleTabClick(actions, widget),
  handleModalTooltipClose: getHandleModalTooltipClose(actions),
  handleModalTooltipPageClick: getHandleModalTooltipPageClick(actions, state),
  handlePanelNoDataClick: getHandlePanelNoDataClick(actions, widget),
});
